// Import react elements
import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";

// Import core functions
import { getAffiliateDetails, registerNewAccount } from "api/APICoreCalls";

// Import global hooks
import useFormHandler from "global/hooks/useFormHandler";
import { useAppConfig } from "global/hooks/useAppConfig";

// Import components
import CreateButton from "global/components/CreateButton";

// Import icons
import { ShowAllIcon } from "global/strich_icons/StrichIcons";

// Import utility functions
import { DisplayConfirmationDialog } from "global/utilities/DialogBox";
import { GetCookie, SetCookie } from "global/utilities/Cookies";

// Import css style sheet
import styles from "../LoginPage.module.css";



const RegisterForm = ({ onSwitch }) => {



    // Set navigate to useNavigate() which provides a function to programmatically navigate between routes
    const navigate = useNavigate();

    // Get the current registration URL so affiliate referrals can be read and removed cleanly
    const location = useLocation();
    const url_params = new URLSearchParams(location.search);
    const affiliate_referral_url_id = String(url_params.get("ref") || "").trim().toLowerCase();
    const affiliate_referral_cookie_id = String(GetCookie("affiliate_referral_id") || "").trim().toLowerCase();

    // Keep the first available referral ready for validation
    const affiliate_referral_candidate_id = affiliate_referral_cookie_id || affiliate_referral_url_id;

    // Get the current app environment config
    const appConfig = useAppConfig();

    // Store whether each password field should be visible
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);

    // Record when the registration form was first displayed for basic bot timing detection
    const registrationStarted = useRef(performance.now());

    // Extract form-related state and functions from the `useFormHandler` custom hook
    const {

        formValues,     // Stores the current values of form inputs
        setFormValues,  // Direct state setter for storing the validated affiliate referral
        handleChange,   // Function to update form values when inputs change
        handleSubmit,   // Function to handle form submission logic
        fieldErrors,    // Object storing validation errors for individual fields
        formError,      // General error message (e.g. API errors)
        loading,        // Boolean indicating whether the form is submitting
        apiResponse,    // Stores the response from the API after submission

    } = useFormHandler({

        // Default values for the form fields
        initialValues: {

            username: "",
            email: "",
            password: "",
            confirmPassword: "",
            contact_website: "",
            affiliate_id: "",


        },

        // API function to be called on form submission - also add the elapsed form time for basic bot detection
        apiCall: (values) => registerNewAccount({
            ...values,
            form_elapsed_ms: Math.round(performance.now() - registrationStarted.current),
        }),

        // Registration is a public call and must not be passed an existing login token
        passTokenToAPI: false,

        // Validation function to check the form fields before submission
        validate: (values) => {

            // Set errors to empty
            const errors = {};

            // Validation rules
            if (!values.email.includes("@")) errors.email = "Invalid email";
            if (values.password.length < 6) errors.password = "Password must be at least 6 characters";
            if (values.password !== values.confirmPassword) errors.confirmPassword = "Passwords must match";

            // Return validation errors (if any)
            return errors;
        },
    });

    // Validate the selected affiliate referral before using it during registration
    useEffect(() => {

        // Clear the form when there is no referral to validate
        if (!affiliate_referral_candidate_id) {

            setFormValues((previousValues) => ({
                ...previousValues,
                affiliate_id: "",
            }));

            return;

        }

        // Track whether the form is still mounted while validation runs
        let isMounted = true;

        // Remove the affiliate referral from the visible registration URL
        const removeAffiliateReferralFromUrl = () => {

            // Stop when there is no referral in the URL
            if (!affiliate_referral_url_id) { return; }

            const next_params = new URLSearchParams(location.search);
            next_params.delete("ref");

            const next_search = next_params.toString();

            navigate(
                `${location.pathname}${next_search ? `?${next_search}` : ""}`,
                { replace: true }
            );

        };

        // Validate the selected affiliate referral
        const validateAffiliateReferral = async () => {

            const response = await getAffiliateDetails(affiliate_referral_candidate_id);

            // Stop if the form was closed while checking
            if (!isMounted) { return; }

            // Use a valid active affiliate referral
            if (response?.success && response?.results?.affiliate_id) {

                setFormValues((previousValues) => ({
                    ...previousValues,
                    affiliate_id: response.results.affiliate_id,
                }));

                // Store a directly supplied referral only after validation
                if (!affiliate_referral_cookie_id) {
                    SetCookie("affiliate_referral_id", response.results.affiliate_id, 30);
                }

                // The validated referral no longer needs to remain in the URL
                removeAffiliateReferralFromUrl();

                return;

            }

            // Preserve an existing candidate during a temporary network failure
            if (!response?.success && response?.error_message === "Network error") {

                setFormValues((previousValues) => ({
                    ...previousValues,
                    affiliate_id: affiliate_referral_candidate_id,
                }));

                return;

            }

            // Remove an invalid affiliate referral from the form
            setFormValues((previousValues) => ({
                ...previousValues,
                affiliate_id: "",
            }));

            // Remove an invalid saved referral
            if (affiliate_referral_cookie_id === affiliate_referral_candidate_id) {
                SetCookie("affiliate_referral_id", "", -1);
            }

            // Remove the URL referral only when it was the invalid referral being checked
            if (affiliate_referral_url_id === affiliate_referral_candidate_id) {
                removeAffiliateReferralFromUrl();
            }

        };

        // Check the affiliate referral
        validateAffiliateReferral();

        // Stop state updates after the form closes
        return () => {
            isMounted = false;
        };

        // Only rerun when the selected referral changes
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [affiliate_referral_candidate_id]);

    // useEffect runs whenever `apiResponse` changes
    useEffect(() => {

        // Check if there is a response
        if (apiResponse) {

            // Check if it is successful
            if (apiResponse.success) {

                // The referral is now permanently recorded against the account
                SetCookie("affiliate_referral_id", "", -1);

                // Check if registrations are currently being held
                if (appConfig.registrationMode === "hold") {

                    // Display the held registration confirmation
                    DisplayConfirmationDialog(

                        // Dialog message
                        "Thanks for registering. We've saved your registration and we'll email you as soon as StrichSuite is ready.",

                        // Confirm button title
                        "OK",

                        // Cancel button title - empty because we don't want a cancel button
                        ""

                    );

                    // Do not send the user to the login page because the account cannot be activated yet
                    return;

                }

                // Display the normal activation confirmation
                DisplayConfirmationDialog(

                    // Dialog message
                    "We've sent you an activation email. Be sure to check your spam folder.",

                    // Confirm button title
                    "OK",

                    // Cancel button title - empty because we don't want a cancel button
                    "",

                    // Returned response from the dialog box
                    function (is_confirmed) {

                        // Check if the response is TRUE
                        if (is_confirmed) {

                            // Set the prefill email cookie
                            SetCookie("prefill_email", formValues.email, 1);

                            // Navigate to the login page
                            navigate("/login");

                        }
                    }
                );

            }
        }

    }, [apiResponse, appConfig.registrationMode, formValues.email, navigate]);



    return (

        <div>

            <form onSubmit={handleSubmit}>

                {/* Bot detection honeypot - genuine users should never interact with this field */}
                <div style={{ position: "absolute", left: "-10000px", width: "1px", height: "1px", overflow: "clip" }}>
                    <input
                        name="contact_website"
                        type="text"
                        value={formValues.contact_website}
                        onChange={handleChange}
                        tabIndex={-1}
                        autoComplete="off"
                    />
                </div>
                
                <input
                    name="email"
                    type="email"
                    placeholder="Email"
                    value={formValues.email}
                    onChange={handleChange}
                />
                {fieldErrors.email && <p style={{ color: "red" }}>{fieldErrors.email}</p>}

                <br />
                <br />

                <div className={styles.login_password_input_wrap}>

                    <input
                        name="password"
                        type={showPassword ? "text" : "password"}
                        placeholder="Password"
                        value={formValues.password}
                        onChange={handleChange}
                    />

                    <button
                        type="button"
                        className={styles.login_password_toggle}
                        onClick={() => setShowPassword(!showPassword)}
                        title={showPassword ? "Hide password" : "Show password"}
                        aria-label={showPassword ? "Hide password" : "Show password"}
                    >
                        <ShowAllIcon />
                    </button>

                </div>

                {fieldErrors.password && <p style={{ color: "red" }}>{fieldErrors.password}</p>}

                <br />
                <br />

                <div className={styles.login_password_input_wrap}>

                    <input
                        name="confirmPassword"
                        type={showConfirmPassword ? "text" : "password"}
                        placeholder="Confirm Password"
                        value={formValues.confirmPassword}
                        onChange={handleChange}
                    />

                    <button
                        type="button"
                        className={styles.login_password_toggle}
                        onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                        title={showConfirmPassword ? "Hide password" : "Show password"}
                        aria-label={showConfirmPassword ? "Hide password" : "Show password"}
                    >
                        <ShowAllIcon />
                    </button>

                </div>

                {fieldErrors.confirmPassword && <p style={{ color: "red" }}>{fieldErrors.confirmPassword}</p>}

                <br />
                <br />

                {formError && <p style={{ color: "red" }}>{formError}</p>}

                <CreateButton title={loading ? "Registering..." : "Register"} size="l" fullWidth="true" color="#1597e5" type="submit" disabled={loading} />

            </form>

            <br />

            <p>
                Already have an account? <span onClick={() => onSwitch("login")} style={{ cursor: "pointer" }}><b>Log In</b></span>
            </p>

        </div>

    );
};

export default RegisterForm;









